import React, { useCallback, useEffect, useState } from "react";
import { Globe, Loader2, CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Pill } from "@admin/components/Primitives";
import { ListingActions } from "@admin/components/lawyers/ListingActions";
import { fetchListingQueue, notifyApprovalsChanged } from "@admin/services/adminApi";
import { formatLawyerDisplayName } from "@admin/lib/lawyerDisplayName";
import { ADMIN_BASE } from "@admin/constants/route";

const STATUS_COLOR = { approved: "emerald", verified: "emerald", in_review: "amber", not_started: "slate" };

function ListingRow({ lawyer, onChanged }) {
  const pct = lawyer.readiness_percent ?? 0;
  return (
    <li className="px-4 py-3 flex flex-wrap items-center gap-4" data-testid={`listing-row-${lawyer.id}`}>
      <div className="flex-1 min-w-[220px]">
        <a href={`${ADMIN_BASE}/lawyers/${lawyer.id}/review`} data-testid={`listing-lawyer-name-${lawyer.id}`}
          className="text-sm font-bold text-[#0A2540] hover:underline">
          {formatLawyerDisplayName(lawyer.name, lawyer.email)}
        </a>
        <p className="text-xs text-slate-500">{lawyer.email}{lawyer.city ? ` · ${lawyer.city}` : ""}</p>
        {lawyer.listing_revoked_at && (
          <p className="text-[11px] text-rose-600 mt-0.5 inline-flex items-center gap-1" data-testid={`listing-revoked-note-${lawyer.id}`}>
            <RotateCcw size={10} /> Previously unlisted{lawyer.listing_revoke_reason ? `: ${lawyer.listing_revoke_reason}` : ""}
          </p>
        )}
      </div>
      <div className="min-w-[120px]" data-testid={`listing-verification-${lawyer.id}`}>
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Verification</p>
        <Pill color={STATUS_COLOR[lawyer.verification_status] || "emerald"}><CheckCircle2 size={10} className="mr-1" /> Verified</Pill>
      </div>
      <div className="min-w-[160px]" data-testid={`listing-completion-${lawyer.id}`}>
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Profile completion</p>
        <div className="flex items-center gap-2">
          <div className="h-2 w-24 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full ${pct === 100 ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs font-bold text-[#0A2540]" data-testid={`listing-completion-pct-${lawyer.id}`}>{pct}%</span>
        </div>
      </div>
      <ListingActions lawyer={lawyer} onChanged={onChanged} />
    </li>
  );
}

export default function AdminListingApprovals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await fetchListingQueue()).items || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load listing queue"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const onChanged = () => { load(); notifyApprovalsChanged(); };

  return (
    <>
      <PageHeader
        testid="admin-listing-approvals-header"
        title="Listing Approval"
        subtitle="Verified lawyers with a 100% complete profile who are not yet visible to users. Approving the listing makes the profile public and bookable."
        actions={!loading && <span className="whitespace-nowrap"><Pill color={items.length ? "amber" : "blue"}>{items.length} awaiting</Pill></span>}
      />
      <section className="bg-white border border-slate-200 rounded-2xl" data-testid="listing-approvals-card">
        <p className="px-4 pt-4 pb-2 text-xs uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1.5">
          <Globe size={14} /> Verified, awaiting listing
        </p>
        {loading ? (
          <div className="p-8 grid place-items-center text-slate-400" data-testid="listing-loading"><Loader2 className="animate-spin" /></div>
        ) : items.length === 0 ? (
          <p className="px-4 pb-6 pt-2 text-sm text-slate-500" data-testid="listing-empty">No lawyers are waiting for listing approval.</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="listing-list">
            {items.map((l) => <ListingRow key={l.id} lawyer={l} onChanged={onChanged} />)}
          </ul>
        )}
      </section>
    </>
  );
}
